<template>
  <component
    :is="elementTag"
    :class="classes"
    :disabled="elementTag === 'button' && disabled"
    @mouseover="toggleTooltip(true)"
    @mouseleave="toggleTooltip(false)"
  >
    <span class="icon-btn__content">
      <span class="iconfont" :class="icon"></span>
      <transition :name="computedTransition">
        <div v-if="tooltipActive" class="icon-btn__tooltip">
          {{ tooltip }}
        </div>
      </transition>
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    /**
     * Place the button in a disabled state
     */
    disabled?: boolean
    /**
     * The iconfont icon to use
     */
    icon?: string
    /**
     * Use the outlined design variant
     */
    outlined?: boolean
    /**
     * Use the yellow design variant
     */
    yellow?: boolean
    /**
     * Anchor tag attribute that transforms components into `<a href="" />`
     */
    href?: string
    /**
     * Element tag to use for the button. Defaults to `button`
     */
    tag?: string
    /**
     * Button description displayed on top of the button on `mouseover` event.
     */
    tooltip?: string
  }>(),
  {
    tag: 'button',
  },
)

const tooltipActive = ref(false)

const classes = computed(() => {
  return {
    'icon-btn': true,
    'icon-btn--disabled': props.disabled,
    'icon-btn--outlined': props.outlined,
    'icon-btn--yellow': props.yellow,
  }
})

/**
 * Use `scale-transition` when showing the tooltip.
 * Use `fade-transition` when hiding the tooltip.
 */
const computedTransition = computed(() => {
  return tooltipActive.value ? 'scale-transition' : 'fade-transition'
})

const elementTag = computed(() => {
  const tag = (props.href && 'a') || props.tag || 'div'
  return tag
})

/**
 * Hide or show the tooltip when there is a tooltip present.
 */
function toggleTooltip(show: boolean): void {
  if (typeof props.tooltip !== 'undefined') {
    tooltipActive.value = show
  }
}
</script>
